import {ref, watch} from 'vue'
import {Chains, getChainFromId} from './dapp/config.mjs'
import {getErcBalance, bn, getAllowance, getGasPrice, getNaiveBalance} from './dapp/index.mjs'
import { db } from './utils.mjs'
import { humanizeWalletError } from './humanizeWalletError.mjs'

function buildStore(def){
  const r = ref(def)
  const setR = (v) => r.value = v
  return () => [r, setR]
}

/** 首屏与 URL 对齐，避免 watch 先用默认 usdt 多打一笔目标链 USDT（兑换页只需原生） */
function initialCrossTypeFromLocation() {
  if (typeof window === 'undefined') return 'usdt'
  try {
    const h = (window.location.hash || '').split('?')[0].toLowerCase()
    if (h.includes('swap') || h.includes('buygas')) return 'naive'
    const p = String(window.location.pathname || '').toLowerCase()
    if (p === '/swap' || p.endsWith('/swap')) return 'naive'
  } catch {
    void 0
  }
  return 'usdt'
}

const defFromChainId = Number(db.get('chainId', 56))
const defToChainId = defFromChainId != 201022? 201022: 56 
export const useAccount = buildStore('')
export const useChainId = buildStore(defFromChainId)
export const useFromChain = buildStore(getChainFromId(defFromChainId))
export const useToChain = buildStore(getChainFromId(defToChainId))
export const useFromBalance = buildStore(bn(0))
export const useToBalance = buildStore(bn(0))
export const useToNaiveBalance = buildStore(bn(0))
/** 目标链 gasPrice（wei），字符串与链上 / Web3 一致；未拉到时为 '0' 便于各处以 bn 判断 */
export const useToChainGasPrice = buildStore('0')
export const useAllowance = buildStore(null)
export const useExchangeAllowance = buildStore(null)
export const useProvider = buildStore(null)
export const useToBalanceLoading = buildStore(false)
export const useFromBalanceLoading = buildStore(false)
export const useShowMenu = buildStore(false)
export const useShowToast = buildStore(false)
export const useCrossType = buildStore(initialCrossTypeFromLocation())

const defLang = localStorage.getItem('lang')
const lang = ref((defLang && ['zh', 'en'].indexOf(defLang) != -1)? defLang: 'en')
const setLang = (v) => {
  lang.value = v
  localStorage.setItem('lang', v)
}
export const useLang = () => [lang, setLang]

const [account, setAccount] = useAccount()
const [fromBalance, setFromBalance] = useFromBalance()
const [toBalance, setToBalance] = useToBalance()
const [toNaiveBalance, setToNaiveBalance] = useToNaiveBalance()
const [chainId, setChainId] = useChainId()
const [fromChain, setFromChain] = useFromChain()
const [toChain, setToChain] = useToChain()
const [fromLoading, setFromLoading] = useFromBalanceLoading()
const [toLoading, setToLoading] = useToBalanceLoading()
const [allowance, setAllowance] = useAllowance()
const [exchangeAllowance, setExchangeAllowance] = useExchangeAllowance()
const [toChainGasPrice, setToChainGasPrice] = useToChainGasPrice()
const [provider, setProvider] = useProvider()
const [crossType] = useCrossType()

let toChainFetchSeq = 0
let fromChainWatchSeq = 0
let accountWatchSeq = 0

/**
 * 刷新后 MetaMask 等已授权但 store 尚未写入：与顶栏、兑换卡片的「已连接」对齐。
 * - eth_accounts 在扩展注入完成前可能短暂为空，故做少量重试。
 * - 仅当仍存在活跃的 WalletConnect provider（且不是浏览器注入节点）时，才跳过静默 eth_accounts，
 *   避免「曾连过 WC、本地残留 topic」在整页刷新后阻止回填扩展钱包，造成顶栏已由 connectInject 写入地址、
 *   而卡片仍认为未连接的分叉。
 */
export async function syncInjectedAccountIfNeeded() {
  if (typeof window === 'undefined') return
  if (String(account.value || '').trim()) return
  const wt = db.get('wallet_type', 'inject')
  const topic = String(db.get('topic', '') || '').trim()
  const eth = window.ethereum || (window.bitkeep && window.bitkeep.ethereum)
  if (wt === 'wallet_connect' && topic) {
    const p = provider.value
    if (p && p !== eth && typeof p.request === 'function') return
  }
  if (!eth || !eth.request) return
  for (let attempt = 0; attempt < 5; attempt++) {
    if (String(account.value || '').trim()) return
    try {
      const accs = await eth.request({ method: 'eth_accounts' })
      const a = accs && accs[0] && String(accs[0]).trim()
      if (a) {
        setAccount(a)
        setProvider(eth)
        try {
          const hex = await eth.request({ method: 'eth_chainId' })
          const n = typeof hex === 'string' && hex.startsWith('0x') ? parseInt(hex, 16) : Number(hex)
          if (n && getChainFromId(n)) setChainId(n)
        } catch (_e) {
          void _e
        }
        return
      }
    } catch (e) {
      console.warn('[hieswap] syncInjectedAccountIfNeeded', e)
    }
    if (attempt < 4) await new Promise((r) => setTimeout(r, 90 + attempt * 110))
  }
}

async function getGasPriceWithRetry(chainId, attempts = 3) {
  if (!chainId) return '0'
  let lastErr
  for (let i = 0; i < attempts; i++) {
    try {
      const g = await getGasPrice(chainId)
      if (g != null && String(g) !== '' && Number(g) > 0) return String(g)
    } catch (e) {
      lastErr = e
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, 280 * (i + 1)))
    }
  }
  if (lastErr) console.warn('[hieswap] getGasPrice failed', chainId, lastErr)
  return '0'
}

async function refreshToChainPortfolio() {
  const chain = toChain.value
  const cid = chain && chain.chainId
  if (!cid) return
  const seq = ++toChainFetchSeq
  setToLoading(true)
  setToChainGasPrice('0')
  const acc = account.value || ''
  const naive = crossType.value === 'naive'
  try {
    if (naive) {
      const naiveBal = await getNaiveBalance(cid, acc).catch(() => bn(0))
      if (seq !== toChainFetchSeq || toChain.value.chainId !== cid) return
      setToBalance(bn(0))
      setToNaiveBalance(naiveBal)
    } else {
      const [balance, naiveBal] = await Promise.all([
        getErcBalance(cid, acc).catch(() => '0'),
        getNaiveBalance(cid, acc).catch(() => bn(0)),
      ])
      if (seq !== toChainFetchSeq || toChain.value.chainId !== cid) return
      setToBalance(bn(balance))
      setToNaiveBalance(naiveBal)
    }
  } finally {
    if (seq === toChainFetchSeq && toChain.value.chainId === cid) {
      setToLoading(false)
    }
  }
  if (seq !== toChainFetchSeq || toChain.value.chainId !== cid) return
  const gasWei = await getGasPriceWithRetry(cid, 3)
  if (seq === toChainFetchSeq && toChain.value.chainId === cid) {
    setToChainGasPrice(gasWei)
  }
}

watch([toChain, crossType], refreshToChainPortfolio, { immediate: true })
void syncInjectedAccountIfNeeded().then(() => {
  void refreshToChainPortfolio()
})
watch(chainId, () => {
  const fromChain_ = getChainFromId(chainId.value)
  if(!fromChain_){
    throw 'unsupported chain id: ' + chainId.value
  }
  db.set('chainId', chainId.value)
  if(fromChain_.chainId == toChain.value.chainId){
    const savedId = Number(db.get('defToChainId', 0))
    const savedChain =
      savedId && getChainFromId(savedId) && savedId !== fromChain_.chainId
        ? getChainFromId(savedId)
        : null
    const toChian_ = savedChain || (fromChain_.chainId == 201022 ? Chains.bsc : Chains.fsc)
    setToChain(toChian_)
  }
  setFromChain(fromChain_)
})
watch(fromChain, async () => {
  const seq = ++fromChainWatchSeq
  const fc = fromChain.value
  const cid = fc && fc.chainId
  const acc = account.value || ''
  if (!cid) return
  setFromLoading(true)
  try {
    const balance = await getErcBalance(cid, acc).catch(() => '0')
    if (seq !== fromChainWatchSeq || fromChain.value.chainId !== cid) return
    setFromBalance(bn(balance))
    const allowance = await getAllowance(cid, acc).catch(() => '0')
    if (seq !== fromChainWatchSeq || fromChain.value.chainId !== cid) return
    setAllowance(bn(allowance))
    const exSpender = fc && fc.exchangeContract
    const exchangeAllowance = await getAllowance(cid, acc, exSpender).catch(() => '0')
    if (seq !== fromChainWatchSeq || fromChain.value.chainId !== cid) return
    setExchangeAllowance(bn(exchangeAllowance))
  } finally {
    if (seq === fromChainWatchSeq && fromChain.value.chainId === cid) {
      setFromLoading(false)
    }
  }
})
watch(account, async () => {
  const seq = ++accountWatchSeq
  const fc = fromChain.value
  const fromCid = fc && fc.chainId
  const acc = account.value || ''
  if (!fromCid) return
  setFromLoading(true)
  try {
    const balance = await getErcBalance(fromCid, acc).catch(() => '0')
    if (seq !== accountWatchSeq || account.value !== acc || fromChain.value.chainId !== fromCid) return
    setFromBalance(bn(balance))
  } finally {
    if (seq === accountWatchSeq && fromChain.value.chainId === fromCid && account.value === acc) {
      setFromLoading(false)
    }
  }
  const toCid = toChain.value.chainId
  if (!toCid) return
  const naive = crossType.value === 'naive'
  setToLoading(true)
  try {
    if (naive) {
      const tn = await getNaiveBalance(toCid, acc).catch(() => bn(0))
      if (seq !== accountWatchSeq || toChain.value.chainId !== toCid || account.value !== acc) return
      setToBalance(bn(0))
      setToNaiveBalance(tn)
    } else {
      const [tb, tn] = await Promise.all([
        getErcBalance(toCid, acc).catch(() => '0'),
        getNaiveBalance(toCid, acc).catch(() => bn(0)),
      ])
      if (seq !== accountWatchSeq || toChain.value.chainId !== toCid || account.value !== acc) return
      setToBalance(bn(tb))
      setToNaiveBalance(tn)
    }
  } finally {
    if (seq === accountWatchSeq && toChain.value.chainId === toCid && account.value === acc) {
      setToLoading(false)
    }
  }
  if (seq === accountWatchSeq && toChain.value.chainId === toCid && account.value === acc) {
    const gasWei = await getGasPriceWithRetry(toCid, 2)
    if (seq === accountWatchSeq && toChain.value.chainId === toCid && account.value === acc) {
      setToChainGasPrice(gasWei)
    }
  }
})
export const refreshInfo = async () => {
  if(!account.value || !fromChain.value.chainId || !toChain.value.chainId) return;
  const fromBalance = await getErcBalance(fromChain.value.chainId, account.value)
  setFromBalance(bn(fromBalance))
  const allowance = await getAllowance(fromChain.value.chainId, account.value)
  setAllowance(bn(allowance))
  const exchangeAllowance = await getAllowance(fromChain.value.chainId, account.value, fromChain.value.exchangeContract)
  setExchangeAllowance(bn(exchangeAllowance))
  const toCid = toChain.value.chainId
  const naive = crossType.value === 'naive'
  if (naive) {
    const tn = await getNaiveBalance(toCid, account.value).catch(() => bn(0))
    if (toChain.value.chainId === toCid) {
      setToBalance(bn(0))
      setToNaiveBalance(tn)
    }
  } else {
    const [tb, tn] = await Promise.all([
      getErcBalance(toCid, account.value).catch(() => '0'),
      getNaiveBalance(toCid, account.value).catch(() => bn(0)),
    ])
    if (toChain.value.chainId === toCid) {
      setToBalance(bn(tb))
      setToNaiveBalance(tn)
    }
  }
  if (toChain.value.chainId === toCid) {
    setToChainGasPrice(await getGasPriceWithRetry(toCid, 3))
  }
}

const [showToast, setShowToast] = useShowToast()
export const toastMsg = ref('')
export const toast = (v) => {
  let raw = v
  if (v instanceof Error) raw = v.message
  else if (v && typeof v === 'object' && v.message != null) raw = v.message
  else if (typeof v !== 'string') raw = String(v)
  toastMsg.value = humanizeWalletError(raw, lang.value)
  setShowToast(true)
}